import React, { useState, useEffect } from "react";
import { 
  Table, 
  Card, 
  Typography, 
  Tag, 
  Button, 
  Space, 
  Tooltip,
  Modal,
  message
} from "antd";
import { 
  EyeOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  UserOutlined,
  CalendarOutlined,
  HomeOutlined
} from "@ant-design/icons";
import { ActiveGuestmap } from "./types/Guestmap";

const { Title, Text } = Typography;

export const ActiveGuestmapsList: React.FC = () => {
  const [activeGuestmaps, setActiveGuestmaps] = useState<ActiveGuestmap[]>([]);
  const [loading, setLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedGuestmap, setSelectedGuestmap] = useState<ActiveGuestmap | null>(null);

  useEffect(() => {
    loadActiveGuestmaps();
  }, []);

  const loadActiveGuestmaps = async () => {
    try {
      setLoading(true);
      
      // API-Aufruf für aktive Gästemappen
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=getActiveGuestmaps');
      
      if (response.ok) {
        const data = await response.json();
        setActiveGuestmaps(data.guestmaps || []);
      } else {
        message.error('Fehler beim Laden der aktiven Gästemappen');
      }
    } catch (error) {
      console.error('Fehler beim Laden der aktiven Gästemappen:', error);
      message.error('Fehler beim Laden der aktiven Gästemappen');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (guestmap: ActiveGuestmap) => {
    setSelectedGuestmap(guestmap);
    setPreviewModalVisible(true);
  };

  const handleDelete = async (guestmapId: string) => {
    try {
      const response = await fetch('/?type=8847379247&tx_buchungsmanager_frontend[action]=deleteActiveGuestmap', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ guestmapId })
      });

      if (response.ok) {
        message.success('Gästemappe erfolgreich gelöscht');
        loadActiveGuestmaps(); // Liste neu laden
      } else {
        message.error('Fehler beim Löschen der Gästemappe');
      }
    } catch (error) {
      console.error('Fehler beim Löschen:', error);
      message.error('Fehler beim Löschen der Gästemappe');
    }
  };

  const columns = [
    {
      title: 'Gast',
      key: 'guest',
      render: (record: ActiveGuestmap) => (
        <Space>
          <UserOutlined />
          <Text strong>{record.booking.id}</Text>
        </Space>
      ),
    },
    {
      title: 'Zimmer',
      key: 'room',
      render: (record: ActiveGuestmap) => (
        <Space>
          <HomeOutlined />
          <Text>{record.booking.roomNumber} ({record.booking.roomType})</Text>
        </Space>
      ),
    },
    {
      title: 'Anreise',
      key: 'checkIn',
      render: (record: ActiveGuestmap) => (
        <Space>
          <CalendarOutlined />
          <Text>{new Date(record.checkIn).toLocaleDateString('de-DE')}</Text>
        </Space>
      ),
    },
    {
      title: 'Abreise',
      key: 'checkOut',
      render: (record: ActiveGuestmap) => (
        <Space>
          <CalendarOutlined />
          <Text>{new Date(record.checkOut).toLocaleDateString('de-DE')}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (record: ActiveGuestmap) => {
        const now = new Date();
        const checkIn = new Date(record.checkIn);
        const checkOut = new Date(record.checkOut);
        
        let status = 'inactive';
        let color = 'default';
        let text = 'Inaktiv';
        
        if (now >= checkIn && now <= checkOut) {
          status = 'active';
          color = 'green';
          text = 'Aktiv';
        } else if (now < checkIn) {
          status = 'upcoming';
          color = 'blue';
          text = 'Anstehend';
        } else {
          status = 'expired';
          color = 'red';
          text = 'Abgelaufen';
        }
        
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'Aktionen',
      key: 'actions',
      render: (record: ActiveGuestmap) => (
        <Space>
          <Tooltip title="Vorschau">
            <Button 
              type="text" 
              icon={<EyeOutlined />} 
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="Bearbeiten">
            <Button 
              type="text" 
              icon={<EditOutlined />}
              onClick={() => window.open(`/digitale-gaestemappe/bearbeiten/${record.id}`, '_blank')}
            />
          </Tooltip>
          <Tooltip title="Löschen">
            <Button 
              type="text" 
              danger 
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card>
        <div style={{ marginBottom: '24px' }}>
          <Title level={2}>
            <UserOutlined style={{ marginRight: '8px' }} />
            Aktive Gästemappen
          </Title>
          <Text type="secondary">
            Übersicht aller aktiven Gästemappen für derzeitige Gäste
          </Text>
        </div>

        <Table
          columns={columns}
          dataSource={activeGuestmaps}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => 
              `${range[0]}-${range[1]} von ${total} Gästemappen`,
          }}
        />
      </Card>

      {/* Vorschau Modal */}
      <Modal
        title="Gästemappe Vorschau"
        open={previewModalVisible}
        onCancel={() => setPreviewModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewModalVisible(false)}>
            Schließen
          </Button>,
          <Button 
            key="edit" 
            type="primary"
            onClick={() => {
              if (selectedGuestmap) {
                window.open(`/digitale-gaestemappe/bearbeiten/${selectedGuestmap.id}`, '_blank');
              }
              setPreviewModalVisible(false);
            }}
          >
            Bearbeiten
          </Button>
        ]}
        width={800}
      >
        {selectedGuestmap && (
          <div>
            <h3>Gast: {selectedGuestmap.booking.id}</h3>
            <p><strong>Zimmer:</strong> {selectedGuestmap.booking.roomNumber} ({selectedGuestmap.booking.roomType})</p>
            <p><strong>Anreise:</strong> {new Date(selectedGuestmap.checkIn).toLocaleDateString('de-DE')}</p>
            <p><strong>Abreise:</strong> {new Date(selectedGuestmap.checkOut).toLocaleDateString('de-DE')}</p>
            <p><strong>Gäste:</strong> {selectedGuestmap.booking.numberOfGuests}</p>
            <p><strong>Türcode:</strong> {selectedGuestmap.booking.doorCode}</p>
            
            {selectedGuestmap.booking.specialRequests && (
              <div style={{ marginTop: '16px' }}>
                <h4>Besondere Wünsche:</h4>
                <p>{selectedGuestmap.booking.specialRequests}</p>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}; 